import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { isMobile } from 'react-device-detect';
import { IoIosClose } from 'react-icons/io';
import { FiFilter } from 'react-icons/fi';
import { GoHome, GoSearch } from 'react-icons/go';
import {
  Box,
  Flex,
  Button,
  Select,
  Divider,
  Badge,
  Text,
  Group,
  SelectProps,
} from '@mantine/core';
import { useCompaniesSuggestions } from '@worker/hooks/useCompaniesSuggestions';
import { useWorkerProfile } from '@worker/hooks/useWorkerData';
import { useWorkerStore } from '@worker/stores';
import {
  handleMyJobsSearch,
  handleMyJobsClear,
  extractMyJobsUrlParams,
} from '@worker/helpers/my-jobs-helpers';
import { MY_JOBS_PAGE_SIZE } from '@constants/various';
import { MdLocationPin } from 'react-icons/md';
import { PiAirplaneTilt } from 'react-icons/pi';
import SortDropdown, { SortOption, SortOrder } from './SortDropDown';

interface FilterBarProps {
  onSearch: ({
    companyName,
    companyId,
    address,
    miles,
  }: {
    companyName: string;
    companyId: string | null;
    address: google.maps.places.PlaceResult | null;
    miles: number | null;
  }) => void;
  onClear: () => void;

  onToggleAdvancedFilters?: () => void;
  onOpenAdvancedFilters?: () => void;
  showAdvancedFilters?: boolean;
  withAddress?: boolean;
}

function FilterBar({
  onSearch,
  onClear,
  onToggleAdvancedFilters,
  onOpenAdvancedFilters,
  showAdvancedFilters = false,
  withAddress,
}: FilterBarProps) {
  const {
    searchFilters,
    advancedFilters,
    setSearchFilters,
    setAdvancedFilters,
    resetAllFilters,
  } = useWorkerStore();
  const { data: workerProfile } = useWorkerProfile();
  const isInNetwork = useWorkerStore((state) => state.isInNetwork());
  const mainGoogleAddress =
    workerProfile?.worker.address?.googleAddress ?? null;
  const [searchParams, setSearchParams] = useSearchParams();

  // Local state for real-time updates (not saved to store until search)
  const [localCompanyName, setLocalCompanyName] = useState(
    searchFilters.companyName || ''
  );
  const [debouncedCompanySearch, setDebouncedCompanySearch] = useState(
    searchFilters.companyName || ''
  );
  const [localCompanyId, setLocalCompanyId] = useState<string | null>(
    searchFilters.companyId || null
  );
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);

  // Sort options state
  const [selectedSort, setSelectedSort] = useState<SortOption | null>(null);

  const addressIconMap = useMemo(() => {
    const map = new Map<string, 'main' | 'preferred'>();

    if (workerProfile?.worker.address?.googleAddress) {
      map.set(workerProfile.worker.address.googleAddress, 'main');
    }

    workerProfile?.jobPreferences?.preferredAddresses?.forEach((addr) => {
      if (addr.address) {
        map.set(addr.address, 'preferred');
      }
    });

    return map;
  }, [workerProfile]);

  const getAddressForQuery =
    useCallback((): google.maps.places.PlaceResult | null => {
      if (!selectedAddress) {
        return null;
      }

      const mainAddress = workerProfile?.worker?.address;
      if (mainAddress?.googleAddress === selectedAddress) {
        return {
          formatted_address: mainAddress.googleAddress,
          geometry: {
            location: {
              lat: () => mainAddress.latitude,
              lng: () => mainAddress.longitude,
            } as google.maps.LatLng,
          },
        } as google.maps.places.PlaceResult;
      }

      const preferredAddress =
        workerProfile?.jobPreferences?.preferredAddresses?.find(
          (addr) => addr.address === selectedAddress
        );

      if (preferredAddress) {
        return {
          formatted_address: preferredAddress.address,
          geometry: {
            location: {
              lat: () => preferredAddress.addressLatitude,
              lng: () => preferredAddress.addressLongitude,
            } as google.maps.LatLng,
          },
        } as google.maps.places.PlaceResult;
      }

      return null;
    }, [selectedAddress, workerProfile]);

  const getMilesForQuery = useCallback(() => {
    if (!selectedAddress) {
      return null;
    }
    const preferredAddress =
      workerProfile?.jobPreferences?.preferredAddresses?.find(
        (addr) => addr.address === selectedAddress
      );

    if (preferredAddress) {
      return preferredAddress.maxDistanceMiles;
    }
    return workerProfile?.jobPreferences?.maxDistanceMiles || null;
  }, [selectedAddress, workerProfile]);

  // Debounced update for API calls
  const updateDebouncedSearch = useDebouncedCallback((value: string) => {
    setDebouncedCompanySearch(value);
  }, 300);

  const { data: companiesSuggestions } = useCompaniesSuggestions(
    debouncedCompanySearch || ''
  );

  // Initialize filters from URL on mount
  useEffect(() => {
    const urlParams = extractMyJobsUrlParams(searchParams);

    if (urlParams.searchFilters) {
      if (urlParams.searchFilters.companyName) {
        setLocalCompanyName(urlParams.searchFilters.companyName);
      }
      if (urlParams.searchFilters.companyId) {
        setLocalCompanyId(urlParams.searchFilters.companyId);
      }
    }

    // Initialize sort from URL params
    if (urlParams.sort && urlParams.order) {
      setSelectedSort({
        field: urlParams.sort,
        order: urlParams.order as SortOrder.ASC | SortOrder.DESC,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync local state when store filters are reset (e.g., on tab change)
  useEffect(() => {
    setLocalCompanyName(searchFilters.companyName || '');
    setDebouncedCompanySearch(searchFilters.companyName || '');
    setLocalCompanyId(searchFilters.companyId || null);
    if (searchFilters.address?.formatted_address) {
      setSelectedAddress(searchFilters.address.formatted_address);
    } else if (!isInNetwork && mainGoogleAddress) {
      setSelectedAddress(mainGoogleAddress);
    } else {
      setSelectedAddress(null);
    }
  }, [
    searchFilters.companyName,
    searchFilters.companyId,
    searchFilters.address,
    isInNetwork,
    mainGoogleAddress,
  ]);

  const handleSearch = useCallback(() => {
    const address = getAddressForQuery();
    const miles = getMilesForQuery();
    const searchData = {
      companyName: localCompanyName || '',
      companyId: localCompanyId,
      address,
      miles,
    };

    // Save to store when search is clicked
    setSearchFilters(searchData);

    // Update URL with search params (this will trigger query in JobsList)
    handleMyJobsSearch(searchData, advancedFilters, setSearchParams, {
      itemsPerPage: MY_JOBS_PAGE_SIZE,
      existingSearchParams: searchParams,
    });

    // Call legacy callback for compatibility
    onSearch(searchData);
  }, [
    localCompanyName,
    localCompanyId,
    getAddressForQuery,
    getMilesForQuery,
    advancedFilters,
    setSearchFilters,
    setSearchParams,
    searchParams,
    onSearch,
  ]);

  const handleClear = useCallback(() => {
    // Clear local state
    setLocalCompanyName('');
    setDebouncedCompanySearch('');
    setLocalCompanyId(null);
    setSelectedAddress(null);

    // Clear store state (both search and advanced filters)
    resetAllFilters();

    // Clear URL params
    handleMyJobsClear(setSearchParams);

    // Call legacy callback for compatibility
    onClear();
  }, [resetAllFilters, setSearchParams, onClear]);

  // Handle company selection (when user picks from dropdown)
  const handleCompanySelect = (companyId: string | null) => {
    if (companyId) {
      // Find the selected company to get its name
      const selectedCompany = companiesSuggestions?.suggestions?.find(
        (company) => String(company.id) === companyId
      );
      setLocalCompanyId(companyId);
      setLocalCompanyName(selectedCompany?.name || '');
    } else {
      setLocalCompanyId(null);
      setLocalCompanyName('');
    }
  };

  // Handle company search - update UI immediately, debounce API call
  const handleCompanySearchChange = (value: string) => {
    setLocalCompanyName(value);
    updateDebouncedSearch(value);
    if (!value) {
      setLocalCompanyId(null);
    }
  };

  const hasAdvancedFilterLocation = () => {
    return (
      advancedFilters.jobLocation &&
      (advancedFilters.jobLocation.country !== '' ||
        advancedFilters.jobLocation.includeNational === false)
    );
  };

  const hasRateInfo = () => {
    return (
      (advancedFilters.minHourlyEnabled &&
        advancedFilters.minHourlyRate &&
        advancedFilters.minHourlyRate > 0) ||
      (advancedFilters.minDailyEnabled &&
        advancedFilters.minDailyRate &&
        advancedFilters.minDailyRate > 0)
    );
  };

  const renderSearchCompanies = () => {
    return (
      <Select
        value={localCompanyId}
        onChange={handleCompanySelect}
        onSearchChange={handleCompanySearchChange}
        searchValue={localCompanyName}
        leftSection={<GoSearch size={20} />}
        mx={isMobile ? 10 : 0}
        rightSection={[]}
        placeholder="Search by company name"
        searchable
        checkIconPosition="right"
        size="md"
        variant="filled"
        w={isMobile ? undefined : 280}
        data={(companiesSuggestions?.suggestions ?? []).map((company) => ({
          label: company.name,
          value: String(company.id),
        }))}
      />
    );
  };

  const renderOptions: SelectProps['renderOption'] = ({ option }) => {
    let icon = null;

    if (addressIconMap.get(option.value) === 'main') {
      icon = <GoHome size={24} />;
    } else {
      icon = <PiAirplaneTilt size={24} />;
    }

    return (
      <Group wrap="nowrap" gap={8}>
        {icon}
        <Text truncate>{option.label}</Text>
      </Group>
    );
  };

  const renderAddressSelect = () => {
    const getAddressIcon = () => {
      if (!selectedAddress) {
        return <MdLocationPin size={24} />;
      }
      if (addressIconMap.get(selectedAddress) === 'main') {
        return <GoHome size={24} />;
      }
      return <PiAirplaneTilt size={24} />;
    };

    const mainAddressOption = mainGoogleAddress
      ? [{ label: mainGoogleAddress, value: mainGoogleAddress }]
      : [];
    const addressOptions = isInNetwork
      ? Array.from(addressIconMap.keys()).map((address) => ({
          label: address,
          value: String(address),
        }))
      : mainAddressOption;

    return (
      <Box
        mx={10}
        style={{
          maxWidth: isMobile ? '' : '450px',
          width: isMobile ? '' : '100%',
        }}
      >
        <Select
          value={selectedAddress}
          onChange={setSelectedAddress}
          leftSection={getAddressIcon()}
          checkIconPosition="right"
          size="md"
          variant="filled"
          clearable={isInNetwork}
          placeholder={isInNetwork ? 'All addresses' : undefined}
          data={addressOptions}
          renderOption={renderOptions}
          disabled={!withAddress}
        />
      </Box>
    );
  };

  const handleSortOptionChange = (
    field: string,
    order: SortOrder.ASC | SortOrder.DESC
  ) => {
    setSelectedSort({ field, order });
  };

  const handleApplySort = () => {
    if (selectedSort) {
      // Trigger search with sort options
      handleMyJobsSearch(searchFilters, advancedFilters, setSearchParams, {
        sort: selectedSort.field,
        order: selectedSort.order,
        itemsPerPage: MY_JOBS_PAGE_SIZE,
        existingSearchParams: searchParams,
      });
    }
  };

  const renderFilterButtons = () => {
    return (
      <>
        <SortDropdown
          selectedSort={selectedSort}
          onSortChange={handleSortOptionChange}
          onApply={handleApplySort}
        />
        <Button
          variant={showAdvancedFilters ? 'filled' : 'light'}
          fw={700}
          p={5}
          onClick={onToggleAdvancedFilters || onOpenAdvancedFilters}
        >
          <FiFilter size={20} className="m-0 p-0" />
          {!isMobile && (
            <Text tt="uppercase" fw="bold">
              advanced filters
            </Text>
          )}
        </Button>
      </>
    );
  };

  const renderSearchButtons = () => {
    return (
      <Flex gap="xs" mx={10}>
        <Button size={isMobile ? 'xs' : 'md'} fw={700} onClick={handleSearch}>
          Search
        </Button>
        <Button
          size={isMobile ? 'xs' : 'md'}
          variant="light"
          fw={700}
          onClick={handleClear}
        >
          Clear
        </Button>
      </Flex>
    );
  };

  // Helper to update advanced filters and trigger search
  const updateAdvancedFiltersAndSearch = (
    filterUpdates: Partial<typeof advancedFilters>
  ) => {
    const updatedAdvancedFilters = { ...advancedFilters, ...filterUpdates };
    setAdvancedFilters(updatedAdvancedFilters);
    handleMyJobsSearch(searchFilters, updatedAdvancedFilters, setSearchParams, {
      itemsPerPage: MY_JOBS_PAGE_SIZE,
      existingSearchParams: searchParams,
    });
  };

  const handleRemoveLocationBadge = () => {
    updateAdvancedFiltersAndSearch({
      jobLocation: { country: '' as const, includeNational: true },
    });
  };

  const handleRemoveTitleBadge = () => {
    updateAdvancedFiltersAndSearch({ jobTitles: [] });
  };

  const handleRemoveRateBadge = () => {
    updateAdvancedFiltersAndSearch({
      minHourlyEnabled: false,
      minHourlyRate: undefined,
      minDailyEnabled: false,
      minDailyRate: undefined,
    });
  };

  const handleRemoveDateBadge = () => {
    updateAdvancedFiltersAndSearch({
      startDate: null,
      endDate: null,
    });
  };

  const renderMobileBadgesRow = () => {
    const hasLocationFilter = hasAdvancedFilterLocation();
    const hasTitleFilter = advancedFilters.jobTitles?.length > 0;
    const hasRateFilter = hasRateInfo();
    const hasDateFilter = advancedFilters.startDate || advancedFilters.endDate;

    const hasBadges =
      hasLocationFilter || hasTitleFilter || hasRateFilter || hasDateFilter;

    if (!hasBadges) return null;

    return (
      <Flex gap={5} wrap="wrap" mx={10}>
        {hasLocationFilter && (
          <Badge
            color="var(--light-silver)"
            pe={2}
            rightSection={
              <IoIosClose
                color="black"
                size={16}
                style={{ cursor: 'pointer' }}
                onClick={handleRemoveLocationBadge}
              />
            }
          >
            <Text fw={700} size="xs" tt="capitalize" c="black">
              Location
            </Text>
          </Badge>
        )}
        {hasTitleFilter && (
          <Badge
            color="var(--light-silver)"
            pe={2}
            rightSection={
              <IoIosClose
                color="black"
                size={16}
                style={{ cursor: 'pointer' }}
                onClick={handleRemoveTitleBadge}
              />
            }
          >
            <Text fw={700} size="xs" tt="capitalize" c="black">
              Title
            </Text>
          </Badge>
        )}
        {hasRateFilter && (
          <Badge
            color="var(--light-silver)"
            pe={2}
            rightSection={
              <IoIosClose
                color="black"
                size={16}
                style={{ cursor: 'pointer' }}
                onClick={handleRemoveRateBadge}
              />
            }
          >
            <Text fw={700} size="xs" tt="capitalize" c="black">
              Rate Info
            </Text>
          </Badge>
        )}
        {hasDateFilter && (
          <Badge
            color="var(--light-silver)"
            pe={2}
            rightSection={
              <IoIosClose
                color="black"
                size={16}
                style={{ cursor: 'pointer' }}
                onClick={handleRemoveDateBadge}
              />
            }
          >
            <Text fw={700} size="xs" tt="capitalize" c="black">
              Date Info
            </Text>
          </Badge>
        )}
      </Flex>
    );
  };

  if (isMobile) {
    return (
      <Flex direction="column" gap="xs" justify="center" pb="xs">
        <Divider mx={10} color="var(--black)" />
        {renderSearchCompanies()}

        {renderAddressSelect()}

        <Flex gap="sm" align="center" justify="center" mx={10}>
          <Button size="sm" fw={700} onClick={handleSearch}>
            Search
          </Button>
          <Button size="sm" variant="light" fw={700} onClick={handleClear}>
            Clear
          </Button>
          <Flex gap="xs" align="center" ml="auto">
            <SortDropdown
              selectedSort={selectedSort}
              onSortChange={handleSortOptionChange}
              onApply={handleApplySort}
            />
            <Button
              variant={showAdvancedFilters ? 'filled' : 'light'}
              w={40}
              h={40}
              fw={700}
              p={4}
              onClick={onToggleAdvancedFilters || onOpenAdvancedFilters}
            >
              <FiFilter size={20} />
            </Button>
          </Flex>
        </Flex>

        {renderMobileBadgesRow()}
      </Flex>
    );
  }

  return (
    <Flex gap="md" my={15} align="center">
      {renderSearchCompanies()}
      {renderAddressSelect()}
      {renderSearchButtons()}
      <Flex gap="md" ml="auto" align="center">
        {renderFilterButtons()}
      </Flex>
    </Flex>
  );
}

export default FilterBar;
